import { lazy as lazyReact } from 'react';
import { Navigate, Routes, Route, createRoutesFromElements } from 'react-router-dom';

import LogoOnlyLayout from '../components/layouts/LogoOnlyLayout';
import { Elements, Loadable } from './components/RoutesComponents';
import AuthGuard from '../guards/AuthGuard';
import GuestGuard from '../guards/GuestGuard';

const lazy = (importFn) => {
  if (import.meta.env.DEV) {
    // Démarre le transform Vite immédiatement (warm-up)
    const promise = importFn();
    // lazyReact reçoit la même Promise déjà démarrée — doit retourner un composant, pas une Promise
    return lazyReact(() => promise);
  }
  return lazyReact(importFn);
};

// JSX évaluée lazily : les composants Loadable sont déclarés plus bas, les
// référencer au module-load lèverait un TDZ ReferenceError. Cache partagé entre
// `Router` et `getRouteObjects()` → source unique de vérité.
let _cachedRoutesElements = null;
const getRoutesElements = () => {
  if (_cachedRoutesElements === null) {
    _cachedRoutesElements = buildRoutesElements();
  }
  return _cachedRoutesElements;
};

let _cachedRouteObjects = null;
/** Représentation `RouteObject[]` exploitable par `matchRoutes()` — cf. `routes/routeAccess.ts`. */
export const getRouteObjects = () => {
  if (_cachedRouteObjects === null) {
    _cachedRouteObjects = createRoutesFromElements(getRoutesElements());
  }
  return _cachedRouteObjects;
};

const Router = () => <Routes>{getRoutesElements()}</Routes>;

const buildRoutesElements = () => (
  <>
    {/* Shared between apps pages */}
    <Route path="/" element={<GuestGuard />} />
    <Route path="candidate" element={<CandidatesPage />} />
    <Route path="choose-company" element={<CompanySelectionPage />} />
    <Route path="confirm-email" element={<ConfirmEmailPage />} />
    <Route path="login" element={<LoginPage />} />
    <Route path="reset-password" element={<ResetPasswordPage />} />
    <Route path="privacy-policy" element={<PrivacyPolicyPage />} />

    {/* Comète OnTime specific pages */}
    <Route path="/pointages/app-disabled" element={<DisabledAppPage />} />
    <Route path="/pointages/login" element={<LoginPage />} />
    <Route
      path="/pointages/device-type"
      element={
        <AuthGuard>
          <DeviceTypeSelectionPage />
        </AuthGuard>
      }
    />
    <Route
      path="/pointages/init"
      element={
        <AuthGuard>
          <InitializationPage />
        </AuthGuard>
      }
    />

    <Route
      path="/pointages"
      element={<Elements accessibleRoles={['manager', 'clocking_device', 'resource', 'client']} isClockingApp />}
    >
      <Route path="/pointages/" element={<ClockingInAndOutPage />} />
      <Route path="home" element={<OnTimeHomePage role="resource" />} />
      <Route path="client-home" element={<OnTimeHomePage role="client" />} />
      <Route path="manager-home" element={<OnTimeHomePage role="manager" />} />
      <Route path="client-planning" element={<OnTimeClientPlanningPage />} />
      <Route path="forgot-clocking-code" element={<ClockingCodeForgottenPage />} />
      <Route path="pairing" element={<SitesSelectionPage />} />
      <Route path="settings" element={<OnTimeSettingsPage />} />
      <Route path="shift" element={<ShiftPage />} />
      <Route path="sites-management" element={<LinkedSitesToDevicePage />} />
      <Route path="clockables-shifts-list" element={<ClockablesShiftsListPage />} />
      <Route path="clockings-shifts-lists" element={<ClockingsShiftsListsPage />} />
      <Route path="planning" element={<ResSchedule />} />
      <Route path="site-planning" element={<OnTimeAgentSitePlanningPage />} />
      <Route path="manager-planning" element={<OnTimeManagerPlanningPage />} />
      <Route path="consignes-documents" element={<ConsignesAndDocumentsPage />} />
      <Route path="recent-activities" element={<RecentActivitiesPage />} />
      <Route path="mce/resource/events" element={<PageResourceFormList />} />
      <Route
        path="mce/resource/events/fill/:formId"
        element={<PageResourceFormFill />}
        handle={{ hideBottomMenu: true }}
      />
      <Route path="mce/resource/patrols" element={<PageResourcePatrolList />} />
      <Route path="mce/resource/patrols/:patrolId" element={<PageResourcePatrolDetail />} />
      <Route path="mce/manager/events" element={<PageManagerEventList />} />
    </Route>

    {/* All spaces pages */}
    <Route path="/" element={<Elements accessibleRoles={['manager', 'manager_partner', 'resource', 'client']} />}>
      <Route path="about" element={<AboutPage />} />
      <Route path="settings" element={<SettingsPage />} />
      <Route path="settings/notifications" element={<NotificationsSettingsPage />} />
      <Route path="settings/password" element={<UserAccountPage />} />
      <Route path="user" element={<Navigate to="user/profile" replace />} />
      <Route path="research-group" element={<ResearchGroupPage />} />
      <Route path="*" element={<Error404Page />} />
    </Route>

    <Route path="*" element={<LogoOnlyLayout />}>
      <Route path="500" element={<Error500Page />} />
      <Route path="*" element={<Error404Page />} />
    </Route>
    <Route path="maintenance" element={<MaintenancePage />} />

    {/* Specific spaces shared pages */}
    <Route path="/" element={<Elements accessibleRoles={['manager', 'manager_partner', 'client']} />}>
      <Route path="resource/resources" element={<ResourcesPage />} />
      <Route path="resource/resource" element={<ResourceSheetPage />} />
      <Route path="resource/resource/documents" element={<Documents />} />
      <Route path="site/site" element={<SiteSheetPage />} />
      <Route path="site-schedule" element={<SiteSchedulePage />} />
    </Route>

    <Route path="/" element={<Elements accessibleRoles={['manager_partner', 'resource']} />}>
      <Route path="demand" element={<DemandPage />} />
      <Route path="demands" element={<DemandsPage />} />
      <Route path="demands/partner" element={<DemandsPage />} />
    </Route>

    {/* Manager pages */}
    <Route path="/" element={<Elements accessibleRoles={['manager']} />}>
      <Route path="clockings/devices" element={<ManageClockingsDevicesPage />} />
      <Route path="clockings/link-device" element={<DeviceAssociationPage />} />
      <Route path="manager" element={<ManDashboard />} />
      <Route path="services" element={<ManServicesList />} />
      <Route path="site/sites" element={<ManSitesList />} />
    </Route>

    {/* MCE — Évènements manager : liste + détail (permission 'event_cancel', 'event_restore', 'mce_read', 'mce_export') */}
    <Route
      path="/"
      element={
        <Elements
          accessibleRoles={['manager', 'resource', 'client']}
          requiredPermissions={['event_cancel', 'event_restore', 'mce_read', 'mce_export']}
        />
      }
    >
      <Route path="mce/manager/events" element={<PageManagerEventList />} />
      <Route path="mce/manager/entry/:entryId" element={<PageManagerFormEntry />} />
    </Route>

    {/* MCE — Formulaires : liste, création, détail (permission form_control) */}
    <Route
      path="/"
      element={<Elements accessibleRoles={['manager', 'resource', 'client']} requiredPermissions={['form_control']} />}
    >
      <Route path="mce/manager/forms" element={<PageManagerFormList />} />
      <Route path="mce/manager/forms/create" element={<PageManagerFormEdit />} handle={{ hideBottomMenu: true }} />
      <Route
        path="mce/manager/forms/update/:formId"
        element={<PageManagerFormEdit />}
        handle={{ hideBottomMenu: true }}
      />
    </Route>

    {/* MCE — Rondes : liste (permission patrol_control) */}
    <Route
      path="/"
      element={
        <Elements accessibleRoles={['manager', 'resource', 'client']} requiredPermissions={['patrol_control']} />
      }
    >
      <Route path="mce/manager/patrols" element={<PageManagerPatrols />} />

      <Route
        path="mce/manager/patrols/create"
        element={<PageManagerPatrolEdit />}
        handle={{ hideBottomMenu: true }}
      />
      <Route
        path="mce/manager/patrols/create/:draftId"
        element={<PageManagerPatrolEdit />}
        handle={{ hideBottomMenu: true }}
      />
      <Route
        path="mce/manager/patrols/update/:patrolId"
        element={<PageManagerPatrolEdit />}
        handle={{ hideBottomMenu: true }}
      />
    </Route>

    {/* Admin */}
    <Route
      path="/"
      element={<Elements accessibleRoles={['manager', 'resource', 'client']} requiredUserProps={['is_admin_link']} />}
    >
      <Route path="admin/permissions" element={<PageAdminPermission />} />
      <Route path="admin/users" element={<PageAdminUsersList />} />
      <Route path="admin/users/edit/:userId" element={<PageAdminUserEdit />} />
    </Route>

    {/* Resource pages */}
    <Route path="/" element={<Elements accessibleRoles={['resource']} />}>
      <Route path="availabilities" element={<ResAvailabilities />} />
      <Route path="missions" element={<ResMissions />} />
      <Route path="resource" element={<ResDashboard />} />
      <Route path="sheet" element={<ResSheet />} />
      <Route path="schedule" element={<ResSchedule />} />
    </Route>

    {/* MCE — Évènements : liste + remplissage (permission event_create) */}
    <Route
      path="/"
      element={<Elements accessibleRoles={['manager', 'resource', 'client']} requiredPermissions={['event_create']} />}
    >
      <Route path="mce/resource/events" element={<PageResourceFormList />} />
      <Route
        path="mce/resource/events/fill/:formId"
        element={<PageResourceFormFill />}
        handle={{ hideBottomMenu: true }}
      />
    </Route>

    {/* MCE — Rondes actives : liste + déclaration (permission patrol_start) */}
    <Route
      path="/"
      element={<Elements accessibleRoles={['manager', 'resource', 'client']} requiredPermissions={['patrol_start']} />}
    >
      <Route path="mce/resource/patrols" element={<PageResourcePatrolList />} />
      <Route path="mce/resource/patrols/:patrolId" element={<PageResourcePatrolDetail />} />
    </Route>

    {/* Client pages */}
    <Route path="/" element={<Elements accessibleRoles={['client']} />}>
      <Route path="invoices" element={<CliInvoices />} />
      <Route path="client" element={<CliDashboard />} />
      <Route path="client/documents" element={<Documents />} />
      <Route path="providers" element={<CompaniesPage />} />
      <Route path="provider" element={<CompanyPage />} />
      <Route path="provider/documents" element={<Documents />} />
      <Route path="quotes" element={<CliQuotes />} />
    </Route>

    {/* Partner pages */}

    <Route path="/" element={<Elements accessibleRoles={['manager_partner']} />}>
      <Route path="orders" element={<ParOrders />} />
      <Route path="orders/order" element={<ParOrder />} />
      <Route path="partner" element={<ParDashboard />} />
      <Route path="partner/myCompany" element={<CompanyPage />} />
      <Route path="partner/myCompany/documents" element={<Documents />} />
      <Route path="partner/partners" element={<CompaniesPage />} />
      <Route path="partner/partner" element={<CompanyPage />} />
      <Route path="partner/partner/documents" element={<Documents />} />
      <Route path="resource/edit-resource" element={<ParEditResource />} />
    </Route>
  </>
);
export default Router;

// COMPONENTS IMPORTS

// Authentication
const ConfirmEmailPage = Loadable(lazy(() => import('../components/pages/authentication/ConfirmEmailPage')));
const ClockingCodeForgottenPage = Loadable(
  lazy(() => import('../components/pages/cometeOnTime/ClockingCodeForgottenPage'))
);
const LoginPage = Loadable(lazy(() => import('../components/pages/authentication/LoginPage')));
const ResetPasswordPage = Loadable(lazy(() => import('../components/pages/authentication/ResetPasswordPage')));
const UserAccountPage = Loadable(lazy(() => import('../components/pages/user/UserAccountPage')));

// Mobile
const CompanySelectionPage = Loadable(lazy(() => import('../components/pages/general/CompanySelectionPage')));

// Clockings
const ClockingInAndOutPage = Loadable(lazy(() => import('../components/pages/cometeOnTime/ClockingInAndOutPage')));
const DisabledAppPage = Loadable(lazy(() => import('../components/pages/cometeOnTime/DisabledAppPage')));
const DeviceTypeSelectionPage = Loadable(
  lazy(() => import('../components/pages/cometeOnTime/DeviceTypeSelectionPage'))
);
const InitializationPage = Loadable(lazy(() => import('../components/pages/cometeOnTime/InitializationPage')));
const ShiftPage = Loadable(lazy(() => import('../components/pages/cometeOnTime/ShiftPage')));
const SitesSelectionPage = Loadable(lazy(() => import('../components/pages/cometeOnTime/SitesSelectionPage')));
const OnTimeSettingsPage = Loadable(lazy(() => import('../components/pages/cometeOnTime/SettingsPage')));
const LinkedSitesToDevicePage = Loadable(
  lazy(() => import('../components/pages/cometeOnTime/LinkedSitesToDevicePage'))
);
const OnTimeHomePage = Loadable(lazy(() => import('../components/pages/cometeOnTime/OnTimeHomePage')));
const OnTimeClientPlanningPage = Loadable(
  lazy(() => import('../components/pages/cometeOnTime/OnTimeClientPlanningPage'))
);
const ConsignesAndDocumentsPage = Loadable(
  lazy(() => import('../components/pages/cometeOnTime/ConsignesAndDocumentsPage'))
);
const RecentActivitiesPage = Loadable(lazy(() => import('../components/pages/cometeOnTime/RecentActivitiesPage')));
const ClockablesShiftsListPage = Loadable(
  lazy(() => import('../components/pages/cometeOnTime/ClockablesShiftsListPage'))
);
const ClockingsShiftsListsPage = Loadable(
  lazy(() => import('../components/pages/cometeOnTime/ClockingsShiftsListsPage'))
);
const OnTimeAgentSitePlanningPage = Loadable(
  lazy(() => import('../components/pages/cometeOnTime/OnTimeAgentSitePlanningPage'))
);
const OnTimeManagerPlanningPage = Loadable(
  lazy(() => import('../components/pages/cometeOnTime/OnTimeManagerPlanningPage'))
);

// General
const AboutPage = Loadable(lazy(() => import('../components/pages/general/AboutPage/AboutPage')));
const PrivacyPolicyPage = Loadable(lazy(() => import('../pages/general/PrivacyPolicyPage/PrivacyPolicyPage')));
const ResearchGroupPage = Loadable(lazy(() => import('../pages/general/ResearchGroupPage/ResearchGroupPage')));
const CompaniesPage = Loadable(lazy(() => import('../components/pages/general/CompaniesPage')));
const CompanyPage = Loadable(lazy(() => import('../components/pages/general/CompanyPage')));
const DemandPage = Loadable(lazy(() => import('../components/pages/general/DemandPage')));
const DemandsPage = Loadable(lazy(() => import('../components/pages/general/DemandsPage')));
const Documents = Loadable(lazy(() => import('../components/pages/general/DocumentsPage')));
const MaintenancePage = Loadable(lazy(() => import('../components/pages/general/MaintenancePage')));
const Error404Page = Loadable(lazy(() => import('../components/pages/general/Error404Page')));
const Error500Page = Loadable(lazy(() => import('../components/pages/general/Error500Page')));
const ResourceSheetPage = Loadable(lazy(() => import('../components/pages/general/ResourceSheetPage')));
const ResourcesPage = Loadable(lazy(() => import('../components/pages/general/ResourcesPage')));
const SettingsPage = Loadable(lazy(() => import('../components/pages/general/SettingsPage')));
const NotificationsSettingsPage = Loadable(lazy(() => import('../components/pages/general/NotificationsSettingsPage')));
const SiteSchedulePage = Loadable(lazy(() => import('../components/pages/general/SiteSchedulePage')));
const SiteSheetPage = Loadable(lazy(() => import('../components/pages/general/SiteSheetPage')));

// CANDIDATE
const CandidatesPage = Loadable(lazy(() => import('../components/pages/candidate/CandidatesPage')));

// MANAGER
const ManageClockingsDevicesPage = Loadable(
  lazy(() => import('../components/pages/cometeOnTime/ManageClockingsDevicesPage'))
);
const DeviceAssociationPage = Loadable(lazy(() => import('../components/pages/cometeOnTime/DeviceAssociationPage')));
const ManDashboard = Loadable(lazy(() => import('../components/pages/manager/ManagerDashboardPage')));
const ManServicesList = Loadable(lazy(() => import('../components/pages/manager/ServicesPage')));
const ManSitesList = Loadable(lazy(() => import('../components/pages/manager/SitesPage')));
const PageManagerFormEdit = Loadable(lazy(() => import('../pages/link/manager/PageManagerFormEdit')));
const PageManagerFormList = Loadable(lazy(() => import('../pages/link/manager/PageManagerFormList')));
const PageManagerPatrols = Loadable(lazy(() => import('../pages/link/manager/PageManagerPatrols')));
const PageManagerPatrolEdit = Loadable(lazy(() => import('../pages/link/manager/PageManagerPatrolEdit')));
const PageManagerFormEntry = Loadable(lazy(() => import('../pages/link/manager/PageManagerFormEntry')));
const PageManagerEventList = Loadable(lazy(() => import('../pages/link/manager/PageManagerEventList')));

// RESOURCE
const ResAvailabilities = Loadable(lazy(() => import('../components/pages/resource/AvailabilitiesPage')));
const ResDashboard = Loadable(lazy(() => import('../components/pages/resource/ResourceDashboardPage')));
const ResMissions = Loadable(lazy(() => import('../components/pages/resource/MissionsPage')));
const ResSchedule = Loadable(lazy(() => import('../components/pages/resource/ResourceSchedulePage')));
const ResSheet = Loadable(lazy(() => import('../components/pages/resource/MyResourceSheetPage')));
const PageResourceFormFill = Loadable(lazy(() => import('../pages/link/resource/PageResourceFormFill')));
const PageResourceFormList = Loadable(lazy(() => import('../pages/link/resource/PageResourceFormList')));
const PageResourcePatrolList = Loadable(lazy(() => import('../pages/link/resource/PageResourcePatrolList')));
const PageResourcePatrolDetail = Loadable(lazy(() => import('../pages/link/resource/PageResourcePatrolDetail')));

// CLIENT
const CliInvoices = Loadable(lazy(() => import('../components/pages/client/InvoicesPage')));
const CliDashboard = Loadable(lazy(() => import('../components/pages/client/ClientDashboardPage')));
const CliQuotes = Loadable(lazy(() => import('../components/pages/client/QuotesPage')));

// PARTNER
const ParDashboard = Loadable(lazy(() => import('../components/pages/partner/PartnerDashboardPage')));
const ParEditResource = Loadable(lazy(() => import('../components/pages/resource/EditResourcePage')));
const ParOrder = Loadable(lazy(() => import('../components/pages/partner/OrderPage')));
const ParOrders = Loadable(lazy(() => import('../components/pages/partner/OrdersPage')));
// ADMIN
const PageAdminPermission = Loadable(lazy(() => import('../pages/link/manager/PageAdminPermission')));
const PageAdminUsersList = Loadable(lazy(() => import('../pages/link/manager/PageAdminUsersList/PageAdminUsersList')));
const PageAdminUserEdit = Loadable(lazy(() => import('../pages/link/manager/PageAdminUserEdit/PageAdminUserEdit')));
